/**
 * API Client with Axios
 * Handles JWT authentication, token refresh, and error handling
 * Uses secure token storage for browser-based token management
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthTokens, ApiResponse } from '../types';
import { getConfig, createLogger } from '../config/environment';
import { secureTokenStorage } from './secure-token-storage';

const logger = createLogger('ApiClient');

/**
 * Token storage interface - can be implemented differently for web/native
 */
export interface TokenStorage {
  getToken(key: string): Promise<string | null>;
  setToken(key: string, value: string): Promise<void>;
  removeToken(key: string): Promise<void>;
}

/**
 * Browser-based token storage (uses localStorage for web)
 */
export class BrowserTokenStorage implements TokenStorage {
  async getToken(key: string): Promise<string | null> {
    try {
      return typeof window !== 'undefined' ? localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  }

  async setToken(key: string, value: string): Promise<void> {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, value);
      }
    } catch {
      logger.warn('Failed to store token in localStorage');
    }
  }

  async removeToken(key: string): Promise<void> {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(key);
      }
    } catch {
      logger.warn('Failed to remove token from localStorage');
    }
  }
}

/**
 * API Client class with JWT authentication support
 */
export class ApiClient {
  private client: AxiosInstance;
  private tokenStorage: TokenStorage;
  private onTokenRefresh: ((tokens: AuthTokens) => void) | null = null;
  private onUnauthorized: (() => void) | null = null;

  constructor(tokenStorage: TokenStorage = new BrowserTokenStorage()) {
    const config = getConfig();

    this.tokenStorage = tokenStorage;

    // Create axios instance
    this.client = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for auth token
    this.client.interceptors.request.use(
      async (config) => {
        // Skip adding auth token ONLY for:
        // 1. Login endpoint (/api/auth/login)
        // 2. Shop registration (POST /api/shops) - for new shop signup ONLY (not /api/shops/*)
        // 3. OTP endpoints (/api/otp/*) - used during signup flow
        // Note: /api/auth/register REQUIRES auth (ADMIN/SUPER_ADMIN only)
        const isShopRegistration = config.url === '/api/shops' && config.method?.toLowerCase() === 'post';
        const isPublicEndpoint = config.url?.includes('/api/auth/login') ||
                                 isShopRegistration ||
                                 config.url?.includes('/api/otp/');

        // 🚨 DETAILED REQUEST LOGGING FOR OTP ENDPOINTS
        if (config.url?.includes('/api/otp/')) {
          console.log('📋 [ApiClient] ====== OTP API REQUEST DETAILS ======');
          console.log('📋 [ApiClient] Method:', config.method?.toUpperCase());
          console.log('📋 [ApiClient] URL:', config.url);
          console.log('📋 [ApiClient] Base URL:', config.baseURL);
          console.log('📋 [ApiClient] Full URL:', `${config.baseURL || ''}${config.url}`);
          console.log('📋 [ApiClient] Headers:');
          console.log(JSON.stringify(config.headers, null, 2));
          console.log('📋 [ApiClient] Request Data (Body):');
          console.log(JSON.stringify(config.data, null, 2));
          console.log('📋 [ApiClient] Is Public Endpoint:', isPublicEndpoint);
          console.log('📋 [ApiClient] ====== END REQUEST DETAILS ======');
        }

        if (!isPublicEndpoint) {
          const token = await this.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            logger.info('✅ Using OAuth token for request:', config.url);
            logger.debug('Token preview:', token?.substring(0, 20) + '...');
          } else {
            logger.warn('⚠️ No token available for authenticated endpoint:', config.url);
          }
        } else {
          logger.info('🔓 Skipping auth token for public endpoint:', config.url);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Skip auto-logout on 401 ONLY for true public endpoints:
        // 1. Login endpoint (/api/auth/login) - failed login is not "unauthorized session"
        // 2. Shop registration (POST /api/shops) - public signup endpoint ONLY (not /api/shops/*)
        // 3. OTP endpoints (/api/otp/*) - used during signup flow
        // 4. Captcha validation (/api/captcha/*) - used before login/signup
        // Note: /api/auth/register is NOT public - requires ADMIN/SUPER_ADMIN auth
        const isShopRegistration = originalRequest.url === '/api/shops' && originalRequest.method?.toLowerCase() === 'post';
        const isPublicAuthEndpoint = originalRequest.url?.includes('/api/auth/login') ||
                                     isShopRegistration ||
                                     originalRequest.url?.includes('/api/otp/') ||
                                     originalRequest.url?.includes('/api/captcha/');

        if (error.response?.status === 401 && !originalRequest._retry && !isPublicAuthEndpoint) {
          originalRequest._retry = true;

          try {
            // Clear tokens and trigger unauthorized callback for authenticated endpoints only
            logger.warn('Received 401 on authenticated endpoint - clearing tokens and triggering unauthorized callback');
            await this.clearTokens();
            if (this.onUnauthorized) {
              this.onUnauthorized();
            }
          } catch (err) {
            logger.error('Error handling 401 response', err);
          }
        } else if (error.response?.status === 401 && isPublicAuthEndpoint) {
          logger.info('Login/signup failed with 401 - invalid credentials (not triggering auto-logout)');
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Set callback for token refresh
   */
  setOnTokenRefresh(callback: (tokens: AuthTokens) => void): void {
    this.onTokenRefresh = callback;
  }

  /**
   * Set callback for unauthorized (401) responses
   */
  setOnUnauthorized(callback: () => void): void {
    this.onUnauthorized = callback;
  }

  /**
   * Store auth tokens securely
   * Stores tokens in localStorage with expiry tracking
   */
  async setTokens(tokens: AuthTokens): Promise<void> {
    try {
      // Store OAuth token if available
      if (tokens.oauthToken) {
        secureTokenStorage.storeAccessToken(
          tokens.oauthToken,
          'oauth',
          tokens.expiresAt ?
            Math.ceil((tokens.expiresAt - Date.now()) / (24 * 60 * 60 * 1000)) :
            90 // Default 90 days
        );
        logger.info('OAuth token stored securely');
      }

      // Store public token if available
      if (tokens.publicAccessToken) {
        secureTokenStorage.storeAccessToken(
          tokens.publicAccessToken,
          'public',
          7 // Public token is 7 days
        );
        logger.info('Public token stored securely');
      }

      // Also store complete token object for compatibility
      await this.tokenStorage.setToken('auth_tokens', JSON.stringify(tokens));

      if (this.onTokenRefresh) {
        this.onTokenRefresh(tokens);
      }

      logger.info('Tokens stored successfully with expiry tracking');
    } catch (error) {
      logger.error('Failed to store tokens', error);
      throw error;
    }
  }

  /**
   * Get stored auth tokens
   */
  async getTokens(): Promise<AuthTokens | null> {
    try {
      const tokensJson = await this.tokenStorage.getToken('auth_tokens');
      return tokensJson ? JSON.parse(tokensJson) : null;
    } catch (error) {
      logger.error('Failed to retrieve tokens', error);
      return null;
    }
  }

  /**
   * Get access token (oauth token if available, fallback to public token)
   * Validates token expiry before returning
   */
  private async getAccessToken(): Promise<string | null> {
    try {
      // Try to get OAuth token first (full access)
      const oauthToken = secureTokenStorage.getAccessToken('oauth');
      logger.debug('🔍 OAuth token from storage:', oauthToken ? 'Found' : 'Not found');

      if (oauthToken && secureTokenStorage.isTokenValid('oauth')) {
        logger.debug('✅ Using valid OAuth token');
        return oauthToken;
      }

      // Fallback to public token if OAuth is not available
      const publicToken = secureTokenStorage.getAccessToken('public');
      logger.debug('🔍 Public token from storage:', publicToken ? 'Found' : 'Not found');

      if (publicToken && secureTokenStorage.isTokenValid('public')) {
        logger.debug('✅ Using valid public token');
        return publicToken;
      }

      // If no valid token found, check stored tokens for fallback
      const tokens = await this.getTokens();
      if (!tokens) {
        logger.warn('⚠️ No tokens available in storage');
        return null;
      }

      const token = tokens.oauthToken || tokens.publicAccessToken;
      return token || null;
    } catch (error) {
      logger.error('Failed to get access token', error);
      return null;
    }
  }

  /**
   * Clear stored tokens and all authentication data
   */
  async clearTokens(): Promise<void> {
    try {
      logger.info('🧹 Starting complete logout cleanup...');

      // Clear from token storage
      await this.tokenStorage.removeToken('oauth_token');
      await this.tokenStorage.removeToken('public_token');
      await this.tokenStorage.removeToken('auth_tokens');

      // Clear from secure storage
      secureTokenStorage.clearAllTokens();

      // Clear ALL localStorage items (complete cleanup)
      if (typeof window !== 'undefined') {
        // Get all keys before clearing
        const keysCount = localStorage.length;
        logger.info(`🗑️ Clearing ${keysCount} localStorage items`);

        // Clear all localStorage
        localStorage.clear();

        logger.info('✅ All localStorage cleared');
      }

      // Clear ALL cookies
      if (typeof document !== 'undefined') {
        logger.info('🍪 Clearing all cookies');
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

          // Delete cookie for all paths and domains
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
        }

        logger.info(`✅ Cleared ${cookies.length} cookies`);
      }

      // Clear sessionStorage as well
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const sessionKeysCount = sessionStorage.length;
        logger.info(`🗑️ Clearing ${sessionKeysCount} sessionStorage items`);
        sessionStorage.clear();
        logger.info('✅ All sessionStorage cleared');
      }

      logger.info('✅ Complete logout cleanup successful - all tokens, localStorage, sessionStorage, and cookies cleared');
    } catch (error) {
      logger.error('❌ Failed to clear authentication data', error);
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.get<T>(url, config);
      return response;
    } catch (error) {
      logger.error(`GET ${url} failed`, error);
      throw error;
    }
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.post<T>(url, data, config);
      return response;
    } catch (error) {
      logger.error(`POST ${url} failed`, error);
      throw error;
    }
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.put<T>(url, data, config);
      return response;
    } catch (error) {
      logger.error(`PUT ${url} failed`, error);
      throw error;
    }
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.patch<T>(url, data, config);
      return response;
    } catch (error) {
      logger.error(`PATCH ${url} failed`, error);
      throw error;
    }
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.delete<T>(url, config);
      return response;
    } catch (error) {
      logger.error(`DELETE ${url} failed`, error);
      throw error;
    }
  }
}

// Create singleton instance
let apiClientInstance: ApiClient | null = null;

/**
 * Get or create API client instance
 */
export const getApiClient = (tokenStorage?: TokenStorage): ApiClient => {
  if (!apiClientInstance) {
    apiClientInstance = new ApiClient(tokenStorage);
  }
  return apiClientInstance;
};

/**
 * Reset API client (useful for testing)
 */
export const resetApiClient = (): void => {
  apiClientInstance = null;
};

// Export singleton for convenience
export const apiClient = getApiClient();

